import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { bind } from 'bind-event-listener';
import isEqual from 'lodash/isEqual';
import { useIntl } from 'react-intl-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import { AIEventsInstrumentationProvider, useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { useActivityClient } from '@atlassian/recent-work-client';
import { deleteSessionItem, getSessionItem, SearchDialog, setSessionItem } from '@atlassian/search-dialog';
import { SAIN_USAGE_DWELL_THRESHOLD, SainAction, useSAINUsageCriteriaMet } from '../common/controller/analytics';
import { useSessionId } from '../common/controller/analytics/helpers';
import { AiBrand, CardTemplate, DEFAULT_GAS_CHANNEL, QueryIntent } from '../constants';
import { ErrorBoundary } from '../error-boundary';
import { KnowledgeCard } from '../knowledge-cards';
import { fetchPersonKnowledgeCardData } from '../knowledge-cards/gql/hydrate-people-card';
import { fetchPersonKnowledgeCardDataWithPostFiltering } from '../knowledge-cards/gql/hydrate-people-card/utils';
import { fetchTeamKnowledgeCardData, fetchTeamKnowledgeCardDataWithRanking } from '../knowledge-cards/gql/hydrate-team-card';
import { sha256Hash } from '../utils';
import { AIAnswerContext } from './ai-answer-context';
import { AIAnswerDefaultLoadingWrapper } from './ai-answer-loading/styled';
import { onAIAnswerDialogDroppedOff, onAIAnswerDialogHidden, onAIAnswerDialogQueryIntent, onAIAnswerDialogUnmount, onAIAnswerDialogViewed, onAIAnswerNotShown, onAIAnswerResultShown, onSmartAnswerRouteDetection } from './analytics';
import { DefinitionAnswerCard } from './definition-answer-card/definition-answer-card';
import { aiAnswerDialogMetric, MarkAIAnswerDialogMetricEnd } from './metrics';
import { LegacySmartAnswerCard } from './smart-answer-card/legacy-smart-answer-card';
import { LegacySmartAnswerCardWithoutStreamingExpand } from './smart-answer-card/legacy-smart-answer-card-without-streaming-expand';
import { SmartAnswerCard } from './smart-answer-card/smart-answer-card';
import { SmartAnswerCardWithoutStreamingExpand } from './smart-answer-card/smart-answer-card-without-streaming-expand';
import { NLPSearchCustomDefinitionType, NLPSearchErrorState } from './types';
import { fetchAIAnswer } from './utils/fetchAIAnswer';
import { getKnowledgeCardResultCount } from './utils/knowledgecard-utils';
var searchAiDialogContainerStyles = xcss({
  position: 'relative' // this is for absolute positioned children
});
export var SEARCH_AI_ANSWER_CACHE_KEY = 'atlassian.search-dialog.ai-answer-component';
export var DEFAULT_EMPTY_NLP_SEARCH_STATE = {
  errorState: NLPSearchErrorState.Default,
  format: null,
  disclaimer: null,
  nlpResults: [],
  uniqueSources: []
};
var SearchAIDialogComponent = function SearchAIDialogComponent(_ref) {
  var _nlpSearch$nlpResults, _nlpSearch$nlpFollowU;
  var query = _ref.query,
    setQuery = _ref.setQuery,
    additionalQueryContext = _ref.additionalQueryContext,
    cloudId = _ref.cloudId,
    orgId = _ref.orgId,
    getAIAnswer = _ref.getAIAnswer,
    onNavigate = _ref.onNavigate,
    onClose = _ref.onClose,
    advancedSearchUrl = _ref.advancedSearchUrl,
    searchSessionId = _ref.searchSessionId,
    userDetails = _ref.userDetails,
    wrapper = _ref.wrapper,
    loadingWrapper = _ref.loadingWrapper,
    _ref$source = _ref.source,
    source = _ref$source === void 0 ? 'unknown' : _ref$source,
    _ref$isReadingAids = _ref.isReadingAids,
    isReadingAids = _ref$isReadingAids === void 0 ? false : _ref$isReadingAids,
    readingAidsOptions = _ref.readingAidsOptions,
    _ref$extraAnalyticsAt = _ref.extraAnalyticsAttributes,
    extraAnalyticsAttributes = _ref$extraAnalyticsAt === void 0 ? {} : _ref$extraAnalyticsAt,
    cardProviderProps = _ref.cardProviderProps,
    _ref$baseQuery = _ref.baseQuery,
    baseQuery = _ref$baseQuery === void 0 ? '' : _ref$baseQuery,
    onLoad = _ref.onLoad,
    followUpsEnabled = _ref.followUpsEnabled,
    parsedFilters = _ref.parsedFilters,
    selectedProduct = _ref.selectedProduct,
    answerLoadingRef = _ref.answerLoadingRef,
    smartAnswersRoute = _ref.smartAnswersRoute,
    _ref$cardTemplate = _ref.cardTemplate,
    cardTemplate = _ref$cardTemplate === void 0 ? CardTemplate.DEFAULT : _ref$cardTemplate,
    sourceProduct = _ref.sourceProduct,
    workspaceId = _ref.workspaceId,
    _ref$brand = _ref.brand,
    brand = _ref$brand === void 0 ? AiBrand.AI : _ref$brand,
    closeParentDialog = _ref.closeParentDialog,
    callbacks = _ref.callbacks,
    isAdminHubAIEnabled = _ref.isAdminHubAIEnabled,
    definitionToggleEnabled = _ref.definitionToggleEnabled;
  var intl = useIntl();
  var locale = intl.locale;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    dwellTracked = _useState4[0],
    setDwellTracked = _useState4[1];
  var partialResponseAbortControllerRef = useRef();
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    partialResponse = _useState6[0],
    setPartialResponse = _useState6[1];
  var _useState7 = useState(true),
    _useState8 = _slicedToArray(_useState7, 2),
    tabHasFocus = _useState8[0],
    setTabHasFocus = _useState8[1];
  var _useState9 = useState(null),
    _useState10 = _slicedToArray(_useState9, 2),
    dwellTimer = _useState10[0],
    setDwellTimer = _useState10[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    answerStreamed = _useState12[0],
    setAnswerStreamed = _useState12[1];
  var _useState13 = useState(undefined),
    _useState14 = _slicedToArray(_useState13, 2),
    liked = _useState14[0],
    setLiked = _useState14[1];
  var _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    likesDisabled = _useState16[0],
    setLikesDisabled = _useState16[1];
  var _useState17 = useState(undefined),
    _useState18 = _slicedToArray(_useState17, 2),
    initialReported = _useState18[0],
    setInitialReported = _useState18[1];
  var _useState19 = useState(DEFAULT_EMPTY_NLP_SEARCH_STATE),
    _useState20 = _slicedToArray(_useState19, 2),
    nlpSearch = _useState20[0],
    setData = _useState20[1];
  var filtersRef = useRef(parsedFilters);
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var resultRef = useRef(null);
  var initialLoadTs = useRef(Date.now()).current;
  var loadedTsRef = useRef();
  var dwellTimeRef = useRef();
  var markLoadedTs = function markLoadedTs() {
    loadedTsRef.current = Date.now();
    return loadedTsRef.current - initialLoadTs;
  };
  var startViewportDwell = function startViewportDwell() {
    dwellTimeRef.current = Date.now();
  };
  var markViewportDwell = function markViewportDwell() {
    if (dwellTimeRef.current) {
      return Date.now() - dwellTimeRef.current;
    }
    return 0;
  };
  var _useAIEventsInstrumen = useAIEventsInstrumentation(),
    trackAIInteractionInit = _useAIEventsInstrumen.trackAIInteractionInit,
    trackAIInteractionDismiss = _useAIEventsInstrumen.trackAIInteractionDismiss;
  var sessionId = useSessionId({
    userId: userDetails.id || '',
    cloudId: cloudId,
    source: source
  });
  var activityClient = useActivityClient('v3', 'people');
  var useKnowledgeDiscoveryTeamSearch = fg('ai_use_kd_team_search');
  var useKnowledgeDiscoveryUserSearch = fg('ai_use_kd_user_search');
  var renderCardByExperience = fg('kd_smart_answers_render_by_experience');
  var isAnswered = !loading && nlpSearch.errorState === null;
  var hasError = !loading && !!nlpSearch.errorState && nlpSearch.errorState !== NLPSearchErrorState.Default;
  var answerString = (_nlpSearch$nlpResults = nlpSearch.nlpResults) !== null && _nlpSearch$nlpResults !== void 0 && _nlpSearch$nlpResults.length ? nlpSearch.nlpResults[0].nlpResult : nlpSearch.nlpResult || '';
  var answerFormat = nlpSearch.format;
  var followUps = (_nlpSearch$nlpFollowU = nlpSearch.nlpFollowUpResults) === null || _nlpSearch$nlpFollowU === void 0 ? void 0 : _nlpSearch$nlpFollowU.followUps;
  var sources = nlpSearch.uniqueSources;
  var disclaimer = nlpSearch.disclaimer;
  var answerEditor = nlpSearch.nlpResultEditor;
  var extraAPIAnalyticsAttributes = nlpSearch.extraAPIAnalyticsAttributes;
  var answerCardType = isReadingAids ? 'definition' : smartAnswersRoute === QueryIntent.PERSON ? 'people' : smartAnswersRoute === QueryIntent.TEAM ? 'team' : 'SAIN';
  var commonAttributes = useMemo(function () {
    return {
      cloudId: cloudId,
      orgId: orgId !== null && orgId !== void 0 ? orgId : '',
      queryHash: sha256Hash(query),
      baseQueryHash: sha256Hash(baseQuery),
      query: query,
      appliedFilters: parsedFilters,
      searchSessionId: searchSessionId,
      source: source,
      answerString: answerString,
      answerLength: answerString.length,
      answerWordCount: answerString.split(/\s+/).length,
      answerFormat: answerFormat,
      answerCardType: answerCardType,
      sources: sources,
      followUps: followUps || [],
      errorState: nlpSearch.errorState,
      errorCode: nlpSearch.errorCode,
      extraAttributes: _objectSpread(_objectSpread({
        featureGates: {
          precomputedDefinitionsEnabled: fg('kd_enable_precomputed_definitions')
        }
      }, extraAnalyticsAttributes), extraAPIAnalyticsAttributes),
      apiSource: 'assistance-service',
      workspaceId: workspaceId !== null && workspaceId !== void 0 ? workspaceId : '',
      sessionId: sessionId,
      brand: brand
    };
  }, [cloudId, orgId, query, baseQuery, parsedFilters, searchSessionId, source, answerString, answerFormat, nlpSearch.errorState, nlpSearch.errorCode, extraAnalyticsAttributes, extraAPIAnalyticsAttributes, workspaceId, sessionId, brand, answerCardType, followUps, sources]);
  var fireAnalyticsEvent = useCallback(function (payload) {
    createAnalyticsEvent(payload).fire(DEFAULT_GAS_CHANNEL);
  }, [createAnalyticsEvent]);
  var onNavigateWithMouseEvent = useCallback(function (event) {
    if (!event.ctrlKey && !event.metaKey) {
      onNavigate();
    }
  }, [onNavigate]);
  var getAIAnswerFn = useCallback(function () {
    switch (smartAnswersRoute) {
      case QueryIntent.PERSON:
        return function (_args) {
          return useKnowledgeDiscoveryUserSearch ? fetchPersonKnowledgeCardDataWithPostFiltering({
            tenantId: cloudId,
            personQuery: query,
            intl: intl,
            sourceProduct: sourceProduct
          }) : fetchPersonKnowledgeCardData({
            tenantId: cloudId,
            personQuery: query,
            principalUserId: userDetails.id || '',
            intl: intl
          });
        };
      case QueryIntent.TEAM:
        return function (_args) {
          return useKnowledgeDiscoveryTeamSearch ? fetchTeamKnowledgeCardDataWithRanking({
            activityClient: activityClient,
            orgId: orgId !== null && orgId !== void 0 ? orgId : '',
            siteId: cloudId,
            teamQuery: query,
            sourceProduct: sourceProduct
          }) : fetchTeamKnowledgeCardData({
            activityClient: activityClient,
            orgId: orgId !== null && orgId !== void 0 ? orgId : '',
            tenantId: cloudId,
            teamQuery: query
          });
        };
      default:
      // fallthrough to getAIAnswer
    }
    return getAIAnswer;
  }, [getAIAnswer, smartAnswersRoute, query, orgId, cloudId, activityClient, useKnowledgeDiscoveryUserSearch, useKnowledgeDiscoveryTeamSearch, userDetails.id, intl, sourceProduct]);
  var updateContextCache = useCallback(function (_ref2) {
    var collapsed = _ref2.collapsed,
      liked = _ref2.liked,
      reported = _ref2.reported,
      recentWorkListExpanded = _ref2.recentWorkListExpanded,
      sourcesExpanded = _ref2.sourcesExpanded,
      fromFollowUp = _ref2.fromFollowUp;
    var currentCache = JSON.parse(getSessionItem(SEARCH_AI_ANSWER_CACHE_KEY) || '{}');
    setSessionItem(SEARCH_AI_ANSWER_CACHE_KEY, JSON.stringify({
      query: query,
      additionalContext: additionalQueryContext || '',
      data: nlpSearch,
      collapsed: collapsed === undefined ? currentCache.collapsed : collapsed,
      liked: liked === undefined ? currentCache.liked : liked,
      reported: reported === undefined ? currentCache.reported : reported,
      fromFollowUp: fromFollowUp === undefined ? currentCache.fromFollowUp : fromFollowUp,
      recentWorkListExpanded: recentWorkListExpanded === undefined ? currentCache.recentWorkListExpanded : recentWorkListExpanded,
      sourcesExpanded: sourcesExpanded === undefined ? currentCache.sourcesExpanded : sourcesExpanded
    }));
  }, [query, additionalQueryContext, nlpSearch]);
  useEffect(function () {
    if (answerLoadingRef) {
      answerLoadingRef.current = !!loading;
    }
  }, [loading, answerLoadingRef]);
  useEffect(function () {
    aiAnswerDialogMetric.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  var handleIntersect = function handleIntersect(entries) {
    entries.forEach(function (entry) {
      if (!loading && (isAnswered || hasError)) {
        if (entry.isIntersecting) {
          startViewportDwell();
          fireAnalyticsEvent(onAIAnswerDialogViewed(_objectSpread({}, commonAttributes)));
          if (smartAnswersRoute !== QueryIntent.TEAM && smartAnswersRoute !== QueryIntent.PERSON && !dwellTracked) {
            var usageTimer = setTimeout(function () {
              setDwellTracked(true);
              fireSAINUsageEvent({
                metCriteria: SainAction.DWELL_REACHED,
                dwellTime: markViewportDwell(),
                commonAttributes: commonAttributes
              });
            }, SAIN_USAGE_DWELL_THRESHOLD);
            setDwellTimer(usageTimer);
          }
        } else {
          fireAnalyticsEvent(onAIAnswerDialogHidden(_objectSpread({}, commonAttributes), markViewportDwell()));
        }
      }
    });
  };
  useLayoutEffect(function () {
    var observer = new IntersectionObserver(handleIntersect, {
      threshold: 0.3
    });
    if (resultRef.current && tabHasFocus) {
      observer.observe(resultRef.current);
    }
    return function () {
      observer === null || observer === void 0 || observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, resultRef, isAnswered, hasError, tabHasFocus]);
  useEffect(function () {
    var cached = JSON.parse(getSessionItem(SEARCH_AI_ANSWER_CACHE_KEY) || '{}');
    if ((cached === null || cached === void 0 ? void 0 : cached.query) === query && (cached === null || cached === void 0 ? void 0 : cached.additionalContext) === additionalQueryContext) {
      setData(cached.data);
      if (cached.liked !== undefined) {
        setLiked(cached.liked);
        // if a like is cached, disable likes
        setLikesDisabled(true);
      }
      if (cached.reported !== undefined) {
        setInitialReported(cached.reported);
      }
      setLoading(false);
    } else {
      deleteSessionItem(SEARCH_AI_ANSWER_CACHE_KEY);
      setLoading(true);
      setLiked(undefined);
      setLikesDisabled(false);
      fetchAIAnswer({
        cloudId: cloudId,
        locale: locale,
        query: query,
        additional_context: additionalQueryContext,
        source: source,
        followUpsEnabled: followUpsEnabled && !cached.fromFollowUp,
        getAIAnswer: getAIAnswerFn(),
        setLoading: setLoading,
        partialResponse: partialResponse,
        partialResponseAbortControllerRef: partialResponseAbortControllerRef,
        setPartialResponse: setPartialResponse,
        setData: setData,
        setAnswerStreamed: setAnswerStreamed,
        parsedFilters: parsedFilters,
        selectedProduct: selectedProduct,
        isReadingAids: isReadingAids,
        fireAnalyticsEvent: fireAnalyticsEvent
      });
    }
    filtersRef.current = parsedFilters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, additionalQueryContext]);
  useEffect(function () {
    if (!isEqual(filtersRef.current, parsedFilters)) {
      deleteSessionItem(SEARCH_AI_ANSWER_CACHE_KEY);
      setLoading(true);
      setLiked(undefined);
      setLikesDisabled(false);
      fetchAIAnswer({
        cloudId: cloudId,
        locale: locale,
        query: query,
        source: source,
        followUpsEnabled: followUpsEnabled,
        getAIAnswer: getAIAnswerFn(),
        setLoading: setLoading,
        partialResponse: partialResponse,
        partialResponseAbortControllerRef: partialResponseAbortControllerRef,
        setPartialResponse: setPartialResponse,
        setData: setData,
        setAnswerStreamed: setAnswerStreamed,
        parsedFilters: parsedFilters,
        selectedProduct: selectedProduct,
        isReadingAids: isReadingAids,
        fireAnalyticsEvent: fireAnalyticsEvent
      });
    }
    filtersRef.current = parsedFilters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedFilters]);
  useEffect(function () {
    if (isAnswered) {
      // if a person or team query returns no data don't log result shown
      var numAddtlWhoMatches = smartAnswersRoute === QueryIntent.PERSON ? getKnowledgeCardResultCount({
        answerString: answerString
      }) : 0;
      if ((smartAnswersRoute === QueryIntent.PERSON || smartAnswersRoute === QueryIntent.TEAM) && getKnowledgeCardResultCount({
        answerString: answerString
      }) < 1) {
        markLoadedTs();
        return;
      }
      fireAnalyticsEvent(onAIAnswerResultShown(_objectSpread(_objectSpread({}, commonAttributes), {}, {
        isStreamed: answerStreamed,
        loadTime: markLoadedTs(),
        extraAttributes: _objectSpread(_objectSpread({}, commonAttributes.extraAttributes), {}, {
          multipleUserResultsShown: numAddtlWhoMatches > 0,
          numAddtlWhoMatches: numAddtlWhoMatches,
          isCuratedDefinition: !!answerEditor,
          isPrecomputedDefinition: nlpSearch.customDefinitionType === NLPSearchCustomDefinitionType.PRECOMPUTED
        })
      })));
      if (numAddtlWhoMatches > 0) {
        fireSAINUsageEvent({
          commonAttributes: commonAttributes
        });
      }
    } else if (hasError) {
      fireAnalyticsEvent(onAIAnswerNotShown(_objectSpread(_objectSpread({}, commonAttributes), {}, {
        isStreamed: answerStreamed,
        loadTime: markLoadedTs()
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAnswered]);
  useLayoutEffect(function () {
    trackAIInteractionInit();
    fireAnalyticsEvent(onAIAnswerDialogQueryIntent(commonAttributes));
    if (!isReadingAids && query && smartAnswersRoute) {
      fireAnalyticsEvent(onSmartAnswerRouteDetection(commonAttributes, smartAnswersRoute));
    }
    return function () {
      fireAnalyticsEvent(onAIAnswerDialogHidden(_objectSpread({}, commonAttributes), markViewportDwell()));
      fireAnalyticsEvent(onAIAnswerDialogUnmount(_objectSpread(_objectSpread({}, commonAttributes), {}, {
        isReadingAids: isReadingAids,
        sourceProduct: sourceProduct,
        dwellTime: loadedTsRef.current ? Date.now() - loadedTsRef.current : 0,
        loadTime: loadedTsRef.current ? loadedTsRef.current - initialLoadTs : Date.now() - initialLoadTs
      })));
      trackAIInteractionDismiss();
      if (!loadedTsRef.current) {
        fireAnalyticsEvent(onAIAnswerDialogDroppedOff(_objectSpread({}, commonAttributes)));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(function () {
    var handleWindowBlurOrClose = function handleWindowBlurOrClose() {
      if (tabHasFocus && !loading && (isAnswered || hasError)) {
        fireAnalyticsEvent(onAIAnswerDialogHidden(_objectSpread({}, commonAttributes), markViewportDwell()));
      }
      setTabHasFocus(false);
    };
    var handleWindowFocus = function handleWindowFocus() {
      setTabHasFocus(true);
    };
    var removeBlurListener = bind(window, {
      type: 'blur',
      listener: handleWindowBlurOrClose
    });
    var removeFocusListener = bind(window, {
      type: 'focus',
      listener: handleWindowFocus
    });
    var removeCloseListener = bind(window, {
      type: 'beforeunload',
      listener: handleWindowBlurOrClose
    });
    return function () {
      removeBlurListener();
      removeFocusListener();
      removeCloseListener();
      if (dwellTimer) {
        clearTimeout(dwellTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabHasFocus, loading]);
  useLayoutEffect(function () {
    if (onLoad && !loading && (isAnswered || hasError)) {
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAnswered, hasError]);
  var AIDialogWrapper = wrapper ? wrapper : SearchDialog;
  var AILoadingWrapper = loadingWrapper ? loadingWrapper : AIAnswerDefaultLoadingWrapper;
  var renderDefinition = function renderDefinition() {
    return /*#__PURE__*/React.createElement(DefinitionAnswerCard, null);
  };

  // SAIN in FPS
  var renderSearchPageSmartAnswer = function renderSearchPageSmartAnswer() {
    return /*#__PURE__*/React.createElement(AIDialogWrapper, null, fg('kd_sain_enable_expand_when_streaming') ? /*#__PURE__*/React.createElement(SmartAnswerCard, null) : /*#__PURE__*/React.createElement(SmartAnswerCardWithoutStreamingExpand, null));
  };
  var renderSearchPageKnowledgeCard = function renderSearchPageKnowledgeCard() {
    return /*#__PURE__*/React.createElement(AIDialogWrapper, null, /*#__PURE__*/React.createElement(KnowledgeCard, null));
  };

  // Quick search and advanced search
  var renderDefaultSmartAnswer = function renderDefaultSmartAnswer() {
    return fg('kd_sain_enable_expand_when_streaming') ? /*#__PURE__*/React.createElement(LegacySmartAnswerCard, {
      LoadingWrapper: AILoadingWrapper,
      DialogWrapper: AIDialogWrapper
    }) : /*#__PURE__*/React.createElement(LegacySmartAnswerCardWithoutStreamingExpand, {
      LoadingWrapper: AILoadingWrapper,
      DialogWrapper: AIDialogWrapper
    });
  };
  var renderDialog = function renderDialog() {
    switch (cardTemplate) {
      case CardTemplate.SMART_ANSWER:
      case CardTemplate.RIGHT_PANEL:
        return renderSearchPageSmartAnswer();
      // branch out SAIN in FPS
      default:
        return isReadingAids ? renderDefinition() : renderDefaultSmartAnswer();
    }
  };
  var renderExperience = function renderExperience() {
    switch (smartAnswersRoute) {
      case QueryIntent.NATURAL_LANGUAGE_QUERY:
      case QueryIntent.KEYWORD_OR_ACRONYM:
        return renderSearchPageSmartAnswer();
      case QueryIntent.PERSON:
      case QueryIntent.TEAM:
        return renderSearchPageKnowledgeCard();
      default:
        return isReadingAids ? renderDefinition() : renderDefaultSmartAnswer();
    }
  };
  return /*#__PURE__*/React.createElement(AIAnswerContext.Provider, {
    value: {
      loading: loading,
      partialResponse: partialResponse,
      isAnswered: isAnswered,
      hasError: hasError,
      advancedSearchUrl: advancedSearchUrl,
      userDetails: userDetails,
      disclaimer: disclaimer,
      onNavigate: onNavigateWithMouseEvent,
      onClose: onClose,
      updateContextCache: updateContextCache,
      fireAnalyticsEvent: fireAnalyticsEvent,
      setQuery: setQuery,
      setData: setData,
      commonAttributes: commonAttributes,
      liked: liked,
      setLiked: setLiked,
      likesDisabled: likesDisabled,
      setLikesDisabled: setLikesDisabled,
      initialReported: initialReported,
      isReadingAids: isReadingAids,
      readingAidsOptions: readingAidsOptions,
      cardProviderProps: cardProviderProps,
      baseQuery: baseQuery,
      cardTemplate: cardTemplate,
      smartAnswersRoute: smartAnswersRoute,
      brand: brand,
      answerEditor: answerEditor,
      closeParentDialog: closeParentDialog,
      additionalQueryContext: additionalQueryContext,
      callbacks: callbacks,
      isAdminHubAIEnabled: isAdminHubAIEnabled,
      definitionToggleEnabled: definitionToggleEnabled,
      sourceProduct: sourceProduct
    }
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: searchAiDialogContainerStyles,
    ref: resultRef
  }, renderCardByExperience ? renderExperience() : renderDialog()), !loading && (isAnswered || hasError) && /*#__PURE__*/React.createElement(MarkAIAnswerDialogMetricEnd, null));
};
export var SearchAIDialog = /*#__PURE__*/memo(function (props) {
  var isReadingAids = props.isReadingAids,
    source = props.source,
    sourceProduct = props.sourceProduct,
    sourceSubProduct = props.sourceSubProduct,
    smartAnswersRoute = props.smartAnswersRoute,
    userGeneratedAI = props.userGeneratedAI;
  var getUnifiedAIExperienceName = function getUnifiedAIExperienceName(smartAnswersRoute) {
    switch (smartAnswersRoute) {
      case QueryIntent.PERSON:
        return 'Person Card';
      case QueryIntent.TEAM:
        return 'Team Card';
      case QueryIntent.NATURAL_LANGUAGE_QUERY:
      case QueryIntent.KEYWORD_OR_ACRONYM:
      default:
        return 'Plain Text';
    }
  };
  return /*#__PURE__*/React.createElement(ErrorBoundary, props, /*#__PURE__*/React.createElement(AIEventsInstrumentationProvider, {
    config: {
      product: sourceProduct !== null && sourceProduct !== void 0 ? sourceProduct : 'unknown',
      subproduct: sourceSubProduct !== null && sourceSubProduct !== void 0 ? sourceSubProduct : 'unknown',
      aiFeatureName: isReadingAids ? 'aiDefinitions' : 'smartAnswers',
      aiExperienceName: isReadingAids ? undefined : getUnifiedAIExperienceName(smartAnswersRoute),
      proactiveGeneratedAI: userGeneratedAI ? 0 : 1,
      userGeneratedAI: userGeneratedAI ? 1 : 0,
      isAIFeature: 1,
      source: source,
      channel: DEFAULT_GAS_CHANNEL
    }
  }, /*#__PURE__*/React.createElement(SearchAIDialogComponent, props)));
});